import React from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import Tooltip from './Tooltip';
import { COMMENT_AGENTS } from '../../extensions/bundled/task-manager/commentMentions';
import './AiContentChip.css';

/**
 * AI Act Art. 50: the small visible label on a document or task an AI created
 * or edited (the machine-readable marks behind it: services/ai/aiAuthorship.js).
 *
 * `mark` is `aiMarkState(record, currentHtml)`; null renders nothing. Once a
 * person has rewritten the content substantially the chip stays, and its
 * tooltip adds "edited since" — the history is not erased, it is qualified.
 */
export function aiChipText(t, mark) {
  if (!mark) return '';
  const base = mark.source === 'mcp'
    ? t(mark.kind === 'edited' ? 'aiChat.contentMark.editedMcp' : 'aiChat.contentMark.createdMcp', {
      client: (mark.client && COMMENT_AGENTS[mark.client]) || t('aiChat.contentMark.unknownClient'),
    })
    : t(mark.kind === 'edited' ? 'aiChat.contentMark.edited' : 'aiChat.contentMark.created');
  return mark.editedSince ? t('aiChat.contentMark.editedSince', { what: base }) : base;
}

export default function AiContentChip({ mark, className = '' }) {
  const { t } = useTranslation();
  if (!mark) return null;
  const text = aiChipText(t, mark);
  return (
    <Tooltip content={text} side="bottom">
      <span
        className={`ai-content-chip${mark.editedSince ? ' ai-content-chip--since' : ''}${className ? ` ${className}` : ''}`}
        role="note"
        tabIndex={0}
        aria-label={text}
        data-testid="ai-content-chip"
      >
        <Sparkles size={10} strokeWidth={2} aria-hidden />
        {t('aiChat.contentMark.label')}
      </span>
    </Tooltip>
  );
}
